import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useExplorer } from "@/context/ExplorerContext";
import { ImageSlot } from "@/components/lurio/ImageSlot";
import { CTA, Eyebrow } from "@/components/lurio/Primitives";
import { Lines, EASE } from "@/components/lurio/Motion";

export default function Hero() {
  const { t } = useExplorer();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const coverY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "8%"]);
  const coverRot = useTransform(scrollYProgress, [0, 1], [-2, reduce ? -2 : -5]);
  const backY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "14%"]);

  return (
    <section id="hero" ref={ref} data-testid="section-hero" className="relative min-h-[100svh] overflow-hidden px-5 pb-16 pt-24 sm:px-8 lg:px-12 lg:pb-24 lg:pt-28">
      <div className="mx-auto grid w-full max-w-6xl items-center gap-12 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.6 }}>
            <Eyebrow className="mb-5">{t.hero.eyebrow}</Eyebrow>
          </motion.div>
          <h1 className="font-serif text-[2.6rem] font-bold leading-[1.25] sm:leading-[1.25] lg:leading-[1.25] text-blue sm:text-6xl lg:text-[4.6rem]">
            <Lines lines={t.hero.lines} delay={0.3} />
          </h1>
          <motion.p
            data-testid="hero-support"
            className="mt-6 max-w-lg text-[1.08rem] leading-[1.8] text-ink-soft sm:text-lg"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.75 }}
          >
            {t.hero.support}
          </motion.p>
          <motion.div className="mt-8 flex flex-wrap items-center gap-4" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay: 0.85 }}>
            <CTA testId="hero-cta-button" label="كوموندي دابا" subLabel="و الخلاص عند التوصيل" />
            <ul data-testid="hero-trust-chips" className="flex flex-wrap gap-2">
              {t.hero.chips.map((c) => (
                <li key={c} className="rounded-full border border-ink/15 bg-cream-card px-3 py-1.5 text-[0.8rem] font-bold text-ink">
                  {c}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <div className="relative mx-auto w-full max-w-[340px] lg:col-span-5 lg:max-w-none">
          <motion.div style={{ y: backY }} aria-hidden className="absolute inset-x-6 -bottom-4 top-6 rotate-[4deg] rounded-book bg-cream-deep shadow-[0_20px_40px_-30px_rgba(30,42,56,0.5)]" />
          <motion.div style={{ y: coverY, rotate: coverRot }} className="relative">
            <ImageSlot id="cover" eager className="shadow-[0_40px_80px_-36px_rgba(30,42,56,0.6)] ring-1 ring-ink/10" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
